import { Phone } from "@mui/icons-material";
import { Box, IconButton, Typography, Chip, Tooltip } from "@mui/material";
import { keyframes } from "@mui/system";
import { useStore } from "../../store/store";
import { useActiveCallForPhone } from "../../sockets/useActiveCallForPhone";
import { LiveCall } from "../../store/store.types";

const pulseAnimation = keyframes`
    0% {
        box-shadow: 0 0 0 0 rgba(223, 100, 73, 0.6);
    }
    70% {
        box-shadow: 0 0 0 8px rgba(223, 100, 73, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(223, 100, 73, 0);
    }
`;

const buildAgentChipLabel = (activeCall: LiveCall) => {
    const firstName = activeCall.agentUser?.firstName ?? null;
    if (firstName) {
        const label = `Em chamada · ${firstName}`;
        return label;
    }
    return "Em chamada";
};

const buildAgentTooltipText = (activeCall: LiveCall) => {
    const firstName = activeCall.agentUser?.firstName ?? null;
    const lastName = activeCall.agentUser?.lastName ?? null;
    const extension = activeCall.agentExtension ?? null;
    if (firstName && lastName && extension) {
        const text = `Em chamada por ${firstName} ${lastName} (ext ${extension})`;
        return text;
    }
    if (firstName && lastName) {
        const text = `Em chamada por ${firstName} ${lastName}`;
        return text;
    }
    if (extension) {
        const text = `Em chamada (ext ${extension})`;
        return text;
    }
    return "Em chamada";
};

type CallButtonProps = {
    phone: string;
    chipPlacement?: "right" | "below";
};

export const CallButton = ({ phone, chipPlacement = "right" }: CallButtonProps) => {
    const { notifications } = useStore();
    const activeCall = useActiveCallForPhone(phone);

    const handleCopyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(phone);
            notifications.setNotification({
                isOpen: true,
                message: "Número copiado para a área de transferência",
                severity: "success",
            });
        } catch (error) {
            console.log("Error copying to clipboard =>", error);
            notifications.setNotification({
                isOpen: true,
                message: "Erro ao copiar número",
                severity: "error",
            });
        }
    };

    const isOnActiveCall = activeCall !== null;
    const tooltipText = activeCall ? buildAgentTooltipText(activeCall) : "Copiar número";
    const chipLabel = activeCall ? buildAgentChipLabel(activeCall) : null;
    const isVerticalLayout = chipPlacement === "below";

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: isVerticalLayout ? "column" : "row",
                alignItems: isVerticalLayout ? "flex-start" : "center",
                gap: isVerticalLayout ? 0.5 : 1,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title={tooltipText} placement="top" arrow>
                    <IconButton
                        size="small"
                        onClick={handleCopyToClipboard}
                        sx={{
                            padding: 0.5,
                            color: isOnActiveCall ? "#df6449" : "primary.main",
                            animation: isOnActiveCall ? `${pulseAnimation} 1.5s infinite` : "none",
                            borderRadius: "50%",
                        }}
                    >
                        <Phone fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Typography variant="body2" color="primary" sx={{ cursor: "pointer" }} onClick={handleCopyToClipboard}>
                    {phone}
                </Typography>
            </Box>
            {chipLabel && (
                <Chip
                    label={chipLabel}
                    size="small"
                    color="warning"
                    sx={{ height: 22, fontSize: 11 }}
                />
            )}
        </Box>
    );
};
